import { BaseElement } from 'base';
import Swiper from 'swiper-core';

const REDUCE = '(prefers-reduced-motion: reduce)';
const VIEWPORT = '[data-viewport]';
const GAP = '--influencer-feedback-gap';
const OFFSET = '--influencer-feedback-offset';
const CARD = '.influencer-feedback__card';
const SLIDE_SPEED = 300;
const STEPS = { prev: -1, next: 1 };

class InfluencerFeedback extends BaseElement {
  #swiper = null;
  #viewport = null;
  #top = null;
  #reduceMq = null;
  #blurPending = false;

  onConnect() {
    this.#blurPending = false;
    this.#viewport = this.q(VIEWPORT);
    this.#reduceMq = window.matchMedia(REDUCE);
    this.listen(this.#reduceMq, 'change', this.#onReduceChange);
    this.listen(this, 'keydown', this.#onKeydown);
    this.listen(this, 'shopify:block:select', this.#onBlockSelect);
    this.listen(window, 'resize', this.#onWindowResize);
    if (this.#viewport) {
      this.listen(this.#viewport, 'scroll', this.#onScroll);
      this.listen(this.#viewport, 'blur', this.#onViewportBlur);
    }
    this.cleanup(() => this.#unmount());
    this.#mount();
  }

  #mount() {
    if (!this.#viewport) return;
    const offset = this.#readLength(OFFSET);
    this.#swiper = new Swiper(this.#viewport, {
      slidesPerView: 'auto',
      spaceBetween: this.#readLength(GAP),
      slidesOffsetBefore: offset,
      slidesOffsetAfter: offset,
      watchOverflow: true,
      speed: this.#slideSpeed(),
      on: {
        afterInit: instance => this.#syncEdges(instance),
        slideChange: instance => this.#syncEdges(instance),
        fromEdge: instance => this.#syncEdges(instance),
        reachBeginning: instance => this.#syncEdges(instance),
        reachEnd: instance => this.#syncEdges(instance),
        lock: instance => this.#syncEdges(instance),
        unlock: instance => this.#syncEdges(instance),
        resize: instance => this.#onResize(instance)
      }
    });
    this.#bindTop();
  }

  #unmount() {
    this.#swiper?.destroy(true, true);
    this.#swiper = null;
  }

  async #bindTop() {
    const top = this.q('block-top');
    if (!top) return;
    await customElements.whenDefined('block-top');
    if (!this.isConnected) return;
    this.#top = top.run(({ reason }) => {
      if (STEPS[reason]) this.#step(STEPS[reason]);
    });
    this.#syncEdges();
  }

  #onWindowResize() {
    const instance = this.#swiper;
    if (instance && !instance.destroyed) this.#onResize(instance);
  }

  #onResize(instance) {
    const gap = this.#readLength(GAP);
    const offset = this.#readLength(OFFSET);
    if (
      instance.params.spaceBetween !== gap ||
      instance.params.slidesOffsetBefore !== offset
    ) {
      instance.params.spaceBetween = gap;
      instance.params.slidesOffsetBefore = offset;
      instance.params.slidesOffsetAfter = offset;
      instance.update();
    }
    this.#syncEdges(instance);
  }

  #syncEdges(instance = this.#swiper) {
    if (!instance || instance.destroyed) return;
    const locked = instance.isLocked;
    instance.allowTouchMove = !locked;
    this.#top?.setArrowState({
      prevDisabled: locked || instance.isBeginning,
      nextDisabled: locked || instance.isEnd,
      hidden: locked
    });
    this.#syncFocusable(locked);
  }

  #syncFocusable(locked) {
    const viewport = this.#viewport;
    if (!viewport) return;
    if (!locked) {
      if (viewport.getAttribute('tabindex') !== '0') {
        viewport.setAttribute('tabindex', '0');
      }
      return;
    }
    if (!viewport.matches(':focus')) {
      viewport.removeAttribute('tabindex');
      return;
    }
    viewport.setAttribute('tabindex', '-1');
    this.#blurPending = true;
  }

  #onViewportBlur() {
    if (!this.#blurPending) return;
    this.#blurPending = false;
    if (this.#viewport?.getAttribute('tabindex') === '-1') {
      this.#viewport.removeAttribute('tabindex');
    }
  }

  #onScroll() {
    const instance = this.#swiper;
    const viewport = this.#viewport;
    if (!instance || instance.destroyed || !instance.initialized) return;
    if (viewport.scrollLeft === 0) return;
    const box = viewport.getBoundingClientRect();
    const slides = instance.slides;
    let index = 0;
    let widest = -1;
    slides.forEach((slide, position) => {
      const rect = slide.getBoundingClientRect();
      const visible =
        Math.min(rect.right, box.right) - Math.max(rect.left, box.left);
      if (visible > widest) {
        widest = visible;
        index = position;
      }
    });
    viewport.scrollLeft = 0;
    instance.slideTo(index, 0);
  }

  #slideSpeed() {
    return this.#reduceMq?.matches ? 0 : SLIDE_SPEED;
  }

  #onReduceChange() {
    if (this.#swiper) this.#swiper.params.speed = this.#slideSpeed();
  }

  #readLength(name) {
    return parseFloat(getComputedStyle(this).getPropertyValue(name)) || 0;
  }

  #step(direction) {
    if (!this.#swiper || this.#swiper.isLocked) return;
    if (direction > 0) this.#swiper.slideNext();
    else this.#swiper.slidePrev();
  }

  #onKeydown(event) {
    if (!this.#viewport || event.target !== this.#viewport) return;
    if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
    if (!this.#swiper || this.#swiper.isLocked) return;
    event.preventDefault();
    const forward = this.#swiper.rtlTranslate
      ? event.key === 'ArrowLeft'
      : event.key === 'ArrowRight';
    this.#step(forward ? 1 : -1);
  }

  #onBlockSelect(event) {
    const slide = event.target.closest?.(CARD);
    const instance = this.#swiper;
    if (!slide || !this.contains(slide) || !instance || instance.destroyed) {
      return;
    }
    const index = instance.slides.indexOf(slide);
    if (index >= 0) instance.slideTo(index, 0);
  }
}

if (!customElements.get('influencer-feedback')) {
  customElements.define('influencer-feedback', InfluencerFeedback);
}
